import { g as __spread, f as __read, u as useCogniteContext, d as __assign, b as __awaiter, e as __generator } from './chunk-525a49bc.js';
import { DatePicker, Form, Modal, Input, Checkbox, Radio, Button } from 'antd';
import React, { useState, useMemo, useEffect } from 'react';
import { d as defaultTheme, w as withDefaultTheme } from './chunk-2f6aabaf.js';
import { isFunction, range, last } from 'lodash';
import moment from 'moment';
import { d as getGranularityInMS } from './chunk-5dec4ccb.js';
import { saveAs } from 'file-saver';

var Delimiters;
(function (Delimiters) {
    Delimiters["Comma"] = ",";
    Delimiters["Tab"] = "\t";
    Delimiters["Semicolon"] = ";";
})(Delimiters || (Delimiters = {}));
function datapointsToCSV(_a) {
    var data = _a.data, granularity = _a.granularity, _b = _a.aggregate, aggregate = _b === void 0 ? 'average' : _b, _c = _a.delimiter, delimiter = _c === void 0 ? Delimiters.Comma : _c, format = _a.format, formatLabels = _a.formatLabels;
    var labels = data.map(function (_a) {
        var id = _a.id, externalId = _a.externalId;
        var idString = externalId || id.toString();
        if (!formatLabels) {
            return idString;
        }
        var timeseries = formatLabels.timeseries, labelFormatter = formatLabels.labelFormatter;
        var timeseriesById = timeseries.find(function (_a) {
            var tid = _a.id, tExternalId = _a.externalId;
            return tid === id || tExternalId === externalId;
        });
        return timeseriesById ? labelFormatter(timeseriesById) : idString;
    });
    var arrangedData = arrangeDatapointsByTimestamp({
        data: data,
        aggregate: aggregate,
        granularity: granularity,
    });
    var csvStrings = generateCSVStringsArray(arrangedData, delimiter, format);
    return __spread([__spread(['timestamp'], labels).join(delimiter)], csvStrings).join('\r\n');
}
function downloadCSV(src, filename) {
    if (filename === void 0) { filename = 'data.csv'; }
    var blob = new Blob([src], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, filename);
}
function getStartEndAndIterationsTotal(data) {
    var startTimestamp = Infinity;
    var endTimestamp = 0;
    var iterationsTotal = data.reduce(function (acc, _a) {
        var datapoints = _a.datapoints;
        if (datapoints.length) {
            var start = datapoints[0].timestamp.getTime();
            var end = datapoints[datapoints.length - 1].timestamp.getTime();
            if (startTimestamp > start) {
                startTimestamp = start;
            }
            if (endTimestamp < end) {
                endTimestamp = end;
            }
        }
        return datapoints.length + acc;
    }, 0);
    return [startTimestamp, endTimestamp, iterationsTotal];
}
function arrangeDatapointsByTimestamp(_a) {
    var data = _a.data, aggregate = _a.aggregate, granularityString = _a.granularity;
    if (!granularityString) {
        return [];
    }
    var arrangedData = [];
    var granularity = getGranularityInMS(granularityString);
    var pointers = Array(data.length).fill(0);
    var _b = __read(getStartEndAndIterationsTotal(data), 3), startTimestamp = _b[0], endTimestamp = _b[1], iterationsTotal = _b[2];
    var iterationLeft = iterationsTotal;
    var currentTimestamp = startTimestamp;
    var _loop_1 = function () {
        var values = [];
        data.forEach(function (_a, index) {
            var datapoints = _a.datapoints;
            var dpPointer = pointers[index];
            var datapoint = datapoints[dpPointer];
            if (datapoint) {
                var timestamp = datapoints[dpPointer].timestamp.getTime();
                if (timestamp === currentTimestamp) {
                    var value = datapoints[dpPointer][aggregate];
                    values[index] = value.toString();
                    pointers[index]++;
                    iterationLeft--;
                }
            }
        });
        if (values.length) {
            arrangedData.push(__spread([currentTimestamp], values));
        }
        currentTimestamp += granularity;
    };
    while (iterationLeft > 0 && currentTimestamp <= endTimestamp) {
        _loop_1();
    }
    return arrangedData;
}
function generateCSVStringsArray(arrangedData, delimiter, format) {
    return arrangedData.map(function (timestampRow) {
        var timestamp = timestampRow.shift();
        var pointer = format ? moment(timestamp).format(format) : timestamp;
        return __spread([pointer.toString()], timestampRow).join(delimiter);
    });
}

// Copyright 2020 Cognite AS
var defaultStrings = {
    title: 'Export chart data',
    labelRange: 'Range',
    labelGranularity: 'Label Granularity',
    labelGranularityHelp: 'Example inputs: 15s, 1m, 5h, 2d',
    formatTimestamp: 'Format timestamp?',
    formatTimestampHelp: 'e.g. 2018-04-02 12:20:20',
    delimiterLabel: 'Select delimiter',
    delimiterHelp: 'The character that will separate your data fields',
    csvDownload: 'Download as CSV',
    csvDownloadInProgress: 'Download as CSV',
    closeBtn: 'Close',
    imageDownloadLabel: 'Image download',
    imageDownloadBtn: 'Download as SVG',
    /** @deprecated Cell-limit no longer exists. */
    cellLimitErr: 'You hit the limit of {{ cellLimit }} datapoints - some data may be omitted',
};

// TODO: Check tree shaking for TimeseriesDataExport component
var RangePicker = DatePicker.RangePicker;
var CELL_LIMIT = 10000;
var formatData = 'YYYY-MM-DD HH:mm:ss';
var formItemLayoutDefault = {
    labelCol: { xs: { span: 24 }, sm: { span: 8 } },
    wrapperCol: { xs: { span: 24 }, sm: { span: 16 } },
};
var TimeseriesDataExportFC = function (props) {
    var form = props.form, getFieldDecorator = props.form.getFieldDecorator, _a = props.formItemLayout, formItemLayout = _a === void 0 ? formItemLayoutDefault : _a, _b = props.timeseriesIds, timeseriesIds = _b === void 0 ? [] : _b, _c = props.timeseriesExternalIds, timeseriesExternalIds = _c === void 0 ? [] : _c, visible = props.visible, _d = __read(props.defaultTimeRange, 2), startTimestamp = _d[0], endTimestamp = _d[1], granularity = props.granularity, _e = props.modalWidth, modalWidth = _e === void 0 ? 600 : _e, downloadAsSvg = props.downloadAsSvg, hideModal = props.hideModal, fetchCSV = props.fetchCSV, retrieveTimeseries = props.retrieveTimeseries, onError = props.onError, onSuccess = props.onSuccess, _f = props.strings, strings = _f === void 0 ? defaultStrings : _f, labelFormatter = props.labelFormatter, _g = props.defaultFormSelections, defaultFormSelections = _g === void 0 ? {
        readableDataChecked: false,
        selectedDelimiter: Delimiters.Comma,
    } : _g;
    var context = useCogniteContext(Component, true);
    var _h = __read(useState(false), 2), loading = _h[0], setLoading = _h[1];
    var _j = __read(useState([]), 2), series = _j[0], setSeries = _j[1];
    var lang = useMemo(function () { return (__assign(__assign({}, defaultStrings), (isFunction(strings) ? strings(defaultStrings) : strings))); }, [strings]);
    var seriesNumber = timeseriesIds.length + timeseriesExternalIds.length;
    var title = lang.title, labelRange = lang.labelRange, labelGranularity = lang.labelGranularity, labelGranularityHelp = lang.labelGranularityHelp, formatTimestamp = lang.formatTimestamp, formatTimestampHelp = lang.formatTimestampHelp, delimiterLabel = lang.delimiterLabel, delimiterHelp = lang.delimiterHelp, csvDownload = lang.csvDownload, csvDownloadInProgress = lang.csvDownloadInProgress, closeBtn = lang.closeBtn, imageDownloadLabel = lang.imageDownloadLabel, imageDownloadBtn = lang.imageDownloadBtn;
    /**
     * Correct request time frame boundaries to reduce number of redundant chunked calls.
     * Redundant calls appears when provided time range doesn't have datapoints.
     * @param request - datapoints request
     * @return Promise<timerange> - corrected timerange
     */
    var getLimits = function (request) { return __awaiter(void 0, void 0, void 0, function () {
        var min, max, _a, retrieveLatest, retrieve, _b, start, _c, end, ids, _d, startResults, endResults;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    min = Math.min, max = Math.max;
                    _a = context.datapoints, retrieveLatest = _a.retrieveLatest, retrieve = _a.retrieve;
                    _b = request.start, start = _b === void 0 ? 0 : _b, _c = request.end, end = _c === void 0 ? 0 : _c;
                    ids = request.items.map(extractId);
                    return [4 /*yield*/, Promise.all([
                            retrieve(__assign(__assign({}, request), { start: start,
                                end: end, limit: 1 })),
                            retrieveLatest(ids),
                        ])];
                case 1:
                    _d = __read.apply(void 0, [_e.sent(), 2]), startResults = _d[0], endResults = _d[1];
                    start = max(min.apply(void 0, __spread(getTimestamps(startResults))), Number(start));
                    end = min(max.apply(void 0, __spread(getTimestamps(endResults))), Number(end));
                    return [2 /*return*/, { start: start, end: end }];
            }
        });
    }); };
    var extractId = function (either) {
        return 'id' in either
            ? {
                id: either.id,
            }
            : {
                externalId: either.externalId,
            };
    };
    var fetchDataPoints = function (request) { return __awaiter(void 0, void 0, void 0, function () {
        var _a, _b, start, _c, end, numericGranularity, limit, msPerRequest, ranges, chunks, lastChunk, requests, results;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0: return [4 /*yield*/, getLimits(request)];
                case 1:
                    _a = _d.sent(), _b = _a.start, start = _b === void 0 ? 0 : _b, _c = _a.end, end = _c === void 0 ? 0 : _c;
                    numericGranularity = getGranularityInMS(request.granularity);
                    limit = Math.floor(CELL_LIMIT / seriesNumber);
                    msPerRequest = limit * numericGranularity;
                    ranges = range(start, end, msPerRequest);
                    chunks = ranges.map(function (range) { return [
                        range,
                        range + msPerRequest - numericGranularity,
                    ]; });
                    lastChunk = last(chunks);
                    if (lastChunk[1] > end) {
                        lastChunk[1] = end;
                    }
                    requests = chunks
                        .map(function (_a) {
                        var _b = __read(_a, 2), chunkStart = _b[0], chunkEnd = _b[1];
                        return (__assign(__assign({}, request), { start: chunkStart, end: chunkEnd, limit: limit }));
                    })
                        .map(function (params) { return context.datapoints.retrieve(params); });
                    return [4 /*yield*/, Promise.all(requests)];
                case 2:
                    results = (_d.sent());
                    return [2 /*return*/, results.reduce(function (result, datapointsChunk) {
                            return result.map(function (dp, index) {
                                dp.datapoints = __spread(dp.datapoints, datapointsChunk[index].datapoints);
                                return dp;
                            });
                        })];
            }
        });
    }); };
    var getTimestamps = function (arr) {
        return arr
            .filter(function (_a) {
            var datapoints = _a.datapoints;
            return datapoints.length;
        })
            .map(function (_a) {
            var _b = __read(_a.datapoints, 1), item = _b[0];
            return item.timestamp.getTime();
        });
    };
    var fetchCSVCall = function (request, _a) {
        var aggregate = _a.aggregate, delimiter = _a.delimiter, readableDate = _a.readableDate, granularityString = _a.granularity;
        return __awaiter(void 0, void 0, void 0, function () {
            var data, format, formatLabels;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, fetchDataPoints(request)];
                    case 1:
                        data = _b.sent();
                        format = readableDate ? formatData : '';
                        formatLabels = labelFormatter
                            ? {
                                labelFormatter: labelFormatter,
                                timeseries: series,
                            }
                            : undefined;
                        return [2 /*return*/, datapointsToCSV({
                                data: data,
                                aggregate: aggregate,
                                delimiter: delimiter,
                                format: format,
                                formatLabels: formatLabels,
                                granularity: granularityString,
                            })];
                }
            });
        });
    };
    var fetchTimeseries = function () { return __awaiter(void 0, void 0, void 0, function () {
        var fetchTimeseriesCall, timeserieIdsObj, timeseriesList, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    fetchTimeseriesCall = retrieveTimeseries || context.timeseries.retrieve;
                    timeserieIdsObj = __spread(timeseriesIds.map(function (id) { return ({ id: id }); }), timeseriesExternalIds.map(function (externalId) { return ({
                        externalId: externalId,
                    }); }));
                    setLoading(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, fetchTimeseriesCall(timeserieIdsObj)];
                case 2:
                    timeseriesList = _a.sent();
                    setSeries(timeseriesList);
                    setLoading(false);
                    return [3 /*break*/, 4];
                case 3:
                    error_1 = _a.sent();
                    setLoading(false);
                    if (onError) {
                        onError(error_1);
                    }
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    var onSubmit = function (event) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            event.preventDefault();
            form.validateFields(function (_, values) { return __awaiter(void 0, void 0, void 0, function () {
                var _a, start, end, granularityVal, delimiter, readableDate, aggregate, body, opts, csvString, _b, e_1;
                return __generator(this, function (_c) {
                    switch (_c.label) {
                        case 0:
                            _a = __read(values.range, 2), start = _a[0], end = _a[1], granularityVal = values.granularity, delimiter = values.delimiter, readableDate = values.readableDate;
                            aggregate = 'average';
                            body = {
                                items: series.map(function (_a) {
                                    var id = _a.id;
                                    return ({
                                        id: id,
                                        aggregates: [aggregate],
                                    });
                                }),
                                start: start.valueOf(),
                                end: end.valueOf(),
                                granularity: granularityVal,
                            };
                            opts = {
                                aggregate: aggregate,
                                delimiter: delimiter,
                                readableDate: readableDate,
                                granularity: granularityVal,
                            };
                            setLoading(true);
                            _c.label = 1;
                        case 1:
                            _c.trys.push([1, 6, , 7]);
                            if (!fetchCSV) return [3 /*break*/, 3];
                            return [4 /*yield*/, fetchCSV(body, opts)];
                        case 2:
                            _b = _c.sent();
                            return [3 /*break*/, 5];
                        case 3: return [4 /*yield*/, fetchCSVCall(body, opts)];
                        case 4:
                            _b = _c.sent();
                            _c.label = 5;
                        case 5:
                            csvString = _b;
                            setLoading(false);
                            if (onSuccess) {
                                onSuccess();
                            }
                            downloadCSV(csvString);
                            return [3 /*break*/, 7];
                        case 6:
                            e_1 = _c.sent();
                            setLoading(false);
                            if (onError) {
                                onError(e_1);
                            }
                            return [3 /*break*/, 7];
                        case 7: return [2 /*return*/];
                    }
                });
            }); });
            return [2 /*return*/];
        });
    }); };
    useEffect(function () {
        if (!visible || !seriesNumber) {
            return;
        }
        fetchTimeseries();
    }, [visible]);
    return (React.createElement(Modal, { visible: visible, title: title, width: modalWidth, footer: null, onCancel: hideModal },
        React.createElement(Form, { "data-test-id": "form", onSubmit: onSubmit },
            React.createElement(Form.Item, __assign({}, formItemLayout, { label: labelRange }), getFieldDecorator('range', {
                initialValue: [moment(startTimestamp), moment(endTimestamp)],
                rules: [{ required: true }],
            })(React.createElement(RangePicker, { showTime: { format: 'HH:mm' }, format: "YYYY-MM-DD HH:mm", style: { width: '100%' } }))),
            React.createElement(Form.Item, __assign({}, formItemLayout, { label: labelGranularity, help: labelGranularityHelp }), getFieldDecorator('granularity', {
                rules: [{ required: true }],
                initialValue: granularity,
            })(React.createElement(Input, { "data-test-id": "granularity" }))),
            React.createElement(Form.Item, __assign({}, formItemLayout, { label: formatTimestamp, help: formatTimestampHelp }), getFieldDecorator('readableDate', {
                valuePropName: 'checked',
                initialValue: defaultFormSelections.readableDataChecked,
            })(React.createElement(Checkbox, null))),
            React.createElement(Form.Item, __assign({}, formItemLayout, { label: delimiterLabel, help: delimiterHelp }), getFieldDecorator('delimiter', {
                initialValue: defaultFormSelections.selectedDelimiter,
                rules: [{ required: true }],
            })(React.createElement(Radio.Group, null, Object.values(Delimiters).map(function (delimiter) { return (React.createElement(Radio.Button, { key: delimiter, value: delimiter }, delimiter === Delimiters.Tab ? 'tab' : delimiter)); })))),
            React.createElement(Form.Item, { wrapperCol: { sm: { span: 16, offset: 8 } } },
                React.createElement(Button, { "data-test-id": "submit", type: "primary", htmlType: "submit", disabled: loading }, loading ? csvDownloadInProgress : csvDownload),
                React.createElement(Button, { type: "danger", onClick: hideModal, style: { marginLeft: '10px' } }, closeBtn)),
            downloadAsSvg && (React.createElement(Form.Item, __assign({}, formItemLayout, { label: imageDownloadLabel }),
                React.createElement(Button, { onClick: downloadAsSvg }, imageDownloadBtn))))));
};
var Wrapper = Form.create({
    name: 'TimeseriesDataExport',
})(TimeseriesDataExportFC);
Wrapper.defaultProps = {
    theme: defaultTheme,
};
var Component = withDefaultTheme(Wrapper);
Component.displayName = 'TimeseriesDataExport';

export { Component as C, Delimiters as D, downloadCSV as a, arrangeDatapointsByTimestamp as b, datapointsToCSV as d };
